import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Key, Mail, Send, RefreshCw, Copy, CheckCircle, Users, Loader2 } from "lucide-react";
import { API_URL } from "../config/api";
import axios from "axios";

export default function PukManagementPage() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [copiedPuk, setCopiedPuk] = useState(null);
  const [singleEmail, setSingleEmail] = useState("");

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/auth/admin-list-puks`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const copyPuk = (puk, email) => {
    navigator.clipboard.writeText(puk);
    setCopiedPuk(email);
    toast.success("PUK copied!");
    setTimeout(() => setCopiedPuk(null), 2000);
  };

  const sendPukEmail = async (email) => {
    try {
      setSending(email);
      await axios.post(
        `${API_URL}/auth/admin-send-puk-email`,
        { email },
        { headers: getAuthHeaders() }
      );
      toast.success(`PUK email sent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const sendAllPukEmails = async () => {
    if (!confirm("This will send PUK emails to ALL users. Continue?")) return;
    
    try {
      setSendingAll(true);
      const response = await axios.post(
        `${API_URL}/auth/admin-send-puk-emails`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Sent ${response.data.sent_count} PUK emails`);
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} emails failed to send`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send emails");
    } finally {
      setSendingAll(false);
    }
  };

  const regeneratePuk = async (email) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/admin-generate-puk`,
        { email, regenerate: true },
        { headers: getAuthHeaders() }
      );
      toast.success(`New PUK generated for ${email}: ${response.data.puk}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to regenerate PUK");
    }
  };

  const generateAllPuks = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/admin-generate-all-puks`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Generated ${response.data.generated_count} new PUKs`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to generate PUKs");
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto p-6">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-red-500">Admin access required</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-6 h-6" />
              PUK Management
            </CardTitle>
            <CardDescription>
              Manage Personal Unlock Keys (PUKs) for password recovery
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={fetchUsers} variant="outline" disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={generateAllPuks} variant="outline">
                <Key className="w-4 h-4 mr-2" />
                Generate Missing PUKs
              </Button>
              <Button 
                onClick={sendAllPukEmails} 
                disabled={sendingAll}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingAll ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send PUK Emails to All Users
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Key className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-2xl font-bold">{users.filter(u => u.puk).length}</p>
                  <p className="text-sm text-muted-foreground">With PUKs</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Key className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                  <p className="text-2xl font-bold">{users.filter(u => !u.puk).length}</p>
                  <p className="text-sm text-muted-foreground">Missing PUKs</p>
                </CardContent>
              </Card>
            </div>

            {/* Users table */}
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                <p>Loading users...</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>PUK Code</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>{u.full_name || '-'}</TableCell>
                      <TableCell>
                        {u.puk ? (
                          <div className="flex items-center gap-2">
                            <code className="bg-muted px-2 py-1 rounded font-mono">
                              {u.puk}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => copyPuk(u.puk, u.email)}
                            >
                              {copiedPuk === u.email ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-amber-500">Not generated</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => regeneratePuk(u.email)}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            New PUK
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendPukEmail(u.email)}
                            disabled={sending === u.email || !u.puk}
                          >
                            {sending === u.email ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-1" />
                            )}
                            Send Email
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
